// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import errorUtil from 'app/common/lib/errorUtil';
import i18n from 'app/common/lib/i18n';
import LogToServer from 'app/common/lib/LogToServer';
import NuxPickerInterestsGrid from 'app/common/react/components/growth/activation/NuxPicker/NuxPickerInterestsGrid';
import resourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import urlUtil from 'app/common/lib/urlUtil';
import { Experiment } from 'app/common/react/components/experiments';
import { prettyNumberFormat } from 'app/common/lib/locale';
import { safeFormat } from 'app/common/lib/StringUtil';
import { Timer } from 'app/common/lib/TrackingTimer';
import { track } from 'app/common/lib/react/analytics';
import doesContainObjectById, {
  getInterestRecSource,
  calculateTopicsInsertionData,
} from 'app/common/react/components/growth/activation/NuxUtils/NuxUtils';
import {
  type InterestObj,
  type CategoryObj,
  NUX_PICKER,
  DEFAULT_NUM_TO_PRESELECT,
  SIGNUP_TIME_COOKIE_EXPIRATION,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';

type OwnProps = {|
  categorized?: boolean,
  hideFollowedInterests: boolean,
  isRenux?: boolean,
  isSimpleRenux?: boolean,
  gender: string,
  handleBottomScroll: (isBottom: boolean) => void,
  limited?: boolean,
  numberToFollow: ?number,
  onSelectedInterestsChange: (newInterests: Array<InterestObj>, isSelected: boolean) => void,
  preselect: boolean,
  selectedInterests: Array<InterestObj>,
  setShortenedRenuxContainer: () => void,
  showTableOfContents?: boolean,
  keepFollowingEnabledGroup?: boolean,
  inShortenedEnabled?: boolean,
  activateShortenedExpFn?: ?() => string,
|};

type OwnState = {|
  defaultInterests: Array<InterestObj>,
  defaultInterestsSet: Set<string>,
  categoryProps: {
    categories?: Array<CategoryObj>,
    handleInterestClickFn?: (InterestObj) => () => void,
  },
  contextualMap: { [key: string]: Array<InterestObj> },
  addedContextualTopics: Set<string>,
  interestFlipMessages: { [key: string]: string },
  interestsToFlip: Set<string>,
  numTopicsToShow: ?number, // If not set, default to all topics
|};

type StateProps = {|
  mostRecentPinImages: Array<string>,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
|};

type State = {|
  ...OwnState,
|};

function pickRandomString(choices: Array<string>) {
  const numOptions = choices.length;
  const randomIndex = Math.floor(Math.random() * numOptions);
  return choices[randomIndex];
}

function generateRandomNumber(minInt: number, maxInt: number) {
  const numOptions = maxInt - minInt;
  const randomOffset = Math.floor(Math.random() * numOptions);
  return minInt + randomOffset;
}

function generateRandomFlipText() {
  const randomNum = generateRandomNumber(20, 90);
  const numWithThousand = randomNum * 1000;

  // Depending on the locale, numbers are formatted differently.
  // For example, in English 20,000 -> 20k. In Mandarin 20,000 -> 2万
  const { locale } = i18n.getLocaleData();
  const formattedNum = prettyNumberFormat(locale, numWithThousand, {
    shortform: true,
  });

  const flipText = pickRandomString([
    safeFormat(
      i18n.ngettext(
        '{{ formattedNumber }} person collecting ideas about',
        '{{ formattedNumber }} people collecting ideas about',
        numWithThousand,
        'How many people collect ideas about an interest, where interest name is shown underneath this text, and number is formatted in a short form such as 10K or 1万 instead of 10000 when applicable to the locale',
      ),
      {
        formattedNumber: formattedNum,
      },
    ),
    safeFormat(
      i18n.ngettext(
        '{{ formattedNumber }} person saving ideas about',
        '{{ formattedNumber }} people saving ideas about',
        numWithThousand,
        'How many people save ideas about an interest, where interest name is shown underneath this text, and number is formatted in a short form such as 10K or 1万 instead of 10000 when applicable to the locale',
      ),
      {
        formattedNumber: formattedNum,
      },
    ),
    safeFormat(
      i18n.ngettext(
        '{{ formattedNumber }} person sharing ideas about',
        '{{ formattedNumber }} people sharing ideas about',
        numWithThousand,
        'How many people share ideas about an interest, where interest name is shown underneath this text, and number is formatted in a short form such as 10K or 1万 instead of 10000 when applicable to the locale',
      ),
      {
        formattedNumber: formattedNum,
      },
    ),
  ]);
  return flipText;
}

export class NuxPickerInterestsGridContainer extends Component<Props, State> {
  state = {
    defaultInterests: [],
    defaultInterestsSet: new Set(),
    categoryProps: {},
    contextualMap: {},
    addedContextualTopics: new Set(),
    interestFlipMessages: {},
    interestsToFlip: new Set(),
    numTopicsToShow: null, // by default, all topics
  };

  getExtraImageSignal(): Array<string> {
    const { plpImages, blpImages } = calculateTopicsInsertionData();
    const { mostRecentPinImages } = this.props;
    const unauthImages = plpImages.concat(blpImages);
    return mostRecentPinImages.concat(unauthImages);
  }

  setShortenedRenuxExp = (interests: Array<InterestObj>) => {
    const { inShortenedEnabled, activateShortenedExpFn, setShortenedRenuxContainer } = this.props;
    const interestsArePersonalized =
      interests.length > 0 && getInterestRecSource(interests[0]) === 'viewed_recs';
    if (interestsArePersonalized && activateShortenedExpFn) {
      activateShortenedExpFn();
      if (inShortenedEnabled) {
        setShortenedRenuxContainer();
        this.setState({
          numTopicsToShow: 5,
        });
      }
    }
  };

  componentDidMount() {
    const {
      hideFollowedInterests,
      isRenux,
      selectedInterests,
      limited,
      keepFollowingEnabledGroup,
    } = this.props;
    const { interestFlipMessages } = this.state;

    const nuxInterestsResource = resourceFactory.create('NuxInterestsResource', {
      is_renux: isRenux,
      logNetworkTimer: true,
      loggerComponentName: NUX_PICKER,
      extraImageSignal: this.getExtraImageSignal(),
      hideFollowedInterests,
      defaultNuxInterestsOnly: keepFollowingEnabledGroup,
      limited,
      urlParams: urlUtil.getQueryStringParams(),
    });

    // resourceFactory is needed to avoid cached calls to the resourece
    nuxInterestsResource
      .callGet({ showError: false }, { retries: 1 })
      .then((response) => {
        if (response.resource_response && response.resource_response.data) {
          this.logSignupTiming();
          const newShownInterests = this.mapInterestsForDisplay(
            selectedInterests,
            response.resource_response.data,
          );
          this.setShortenedRenuxExp(newShownInterests);
          this.setState({ defaultInterests: newShownInterests });
          if (this.getInterestsWithUpdatedIsFollowed().length > 0) {
            this.calculateCategories();
          }
          const preselectedInterestsIds = new Set();
          newShownInterests.forEach((interest) => {
            if (interest.is_followed === true) {
              preselectedInterestsIds.add(interest.id);
            }
          });
          // If any interests are preselected, we need to add their contextual interests
          if (Object.keys(preselectedInterestsIds).length > 0) {
            this.contextualUpdateInterests(preselectedInterestsIds);
          }

          const updatedFlipMessages = { ...interestFlipMessages };
          newShownInterests.forEach((interest) => {
            if (!interestFlipMessages[interest.id]) {
              updatedFlipMessages[interest.id] = generateRandomFlipText();
            }
          });
          this.setState({
            interestsToFlip: new Set(Object.keys(preselectedInterestsIds)),
            interestFlipMessages: updatedFlipMessages,
          });
        }
      })
      .catch((error) => {
        if (errorUtil) {
          errorUtil.showError(
            error.message ||
              i18n._('Unable to load topics', 'Error message when failed to load topics.'),
          );
        }
      });
  }

  UNSAFE_componentWillUpdate(nextProps: Props, nextState: State) {
    const newRender = this.state.defaultInterests !== nextState.defaultInterests;
    if (newRender) {
      this.renderTimers.unshift(new Timer().start());
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { defaultInterests } = this.state;
    const newRender = defaultInterests !== prevState.defaultInterests;
    if (newRender) {
      if (this.renderTimers.length) {
        this.recordRenderLogToServer();
      }
      this.calculateCategories();
    }

    if (
      defaultInterests.length > 0 &&
      prevState.defaultInterests.length === 0 &&
      this.props.preselect
    ) {
      this.preselectInterests();
    }
  }

  calculateCategories = () => {
    const category_map = {};
    const interests = this.getInterestsWithUpdatedIsFollowed();
    const handleInterestClickFn = this.handleInterestClickFn;

    interests.forEach((item) => {
      const category_name = item.nux_category || '';
      if (Object.keys(category_map).includes(category_name)) {
        category_map[category_name].push(item);
      } else {
        category_map[category_name] = [item];
      }
    });

    const categories = [];

    Object.keys(category_map).forEach((key) => {
      categories.push({ title: key, interests: category_map[key] });
    });

    // verification process
    if (categories.length !== 6) {
      return;
    }

    for (const category of categories) {
      if (category.title === '' || !category.interests || category.interests.length !== 3) {
        return;
      }
    }

    this.setState({
      categoryProps: { categories, handleInterestClickFn },
    });
  };

  getInterestsWithUpdatedIsFollowed(): Array<InterestObj> {
    const { selectedInterests } = this.props;
    const { defaultInterests } = this.state;

    const displayedInterests = defaultInterests.map<InterestObj>((interest) => {
      interest.is_followed = doesContainObjectById(selectedInterests, interest);
      return interest;
    });

    const interestsToDisplayAtEnd = [
      ...selectedInterests.filter(
        (sInterest) => getInterestRecSource(sInterest) === 'type_ahead_search',
      ),
    ];

    return displayedInterests.concat(...interestsToDisplayAtEnd);
  }

  logSignupTiming() {
    const signupTime = storage.localStorage.getItem(PCONST.LOCALSTORAGE_SIGNUP_TIME);
    if (signupTime) {
      const millisecondsPassed = Date.now() - signupTime;
      const validLog = millisecondsPassed < SIGNUP_TIME_COOKIE_EXPIRATION;
      if (validLog) {
        LogToServer.logToServer(
          LogToServer.SERVER_LOG_TYPE.SIGNUP_LOAD,
          LogToServer.HTTP_METHOD.POST,
          { timing: millisecondsPassed, component: 'nux' },
        );
      }
      storage.localStorage.removeItem(PCONST.LOCALSTORAGE_SIGNUP_TIME);
    }
  }

  mapInterestsForDisplay(
    selectedInterests: Array<InterestObj>,
    interestsData: Array<InterestObj>,
  ): Array<InterestObj> {
    const nuxInterests = interestsData.map((interest) => ({
      ...interest,
      is_followed: this.props.keepFollowingEnabledGroup ? false : interest.is_followed,
      label: interest.name,
    }));

    this.props.onSelectedInterestsChange(
      nuxInterests.filter((interest) => interest.is_followed),
      true,
    );

    // Remove interests from nuxInterests that already exist in selectedInterests
    const selectedInterestSet = new Set(selectedInterests.map((interest) => interest.id));
    const dedupedNuxInterests = nuxInterests.filter(
      (interest) => !selectedInterestSet.has(interest.id),
    );
    return selectedInterests.concat(dedupedNuxInterests);
  }

  preselectInterests() {
    const { numberToFollow, onSelectedInterestsChange } = this.props;
    const { defaultInterests } = this.state;
    const numToFollow = numberToFollow || DEFAULT_NUM_TO_PRESELECT;
    const preselectedInterests = defaultInterests.slice(0, numToFollow);
    onSelectedInterestsChange(preselectedInterests, true);
  }

  // SelectedInterests state handled by NuxContainer, then callback is executed.
  // This allows for synchronous actions
  handleInterestClickFn = (newInterest: InterestObj) => () => {
    const { selectedInterests, onSelectedInterestsChange } = this.props;

    const isSelected = doesContainObjectById(selectedInterests, newInterest);
    const newSelectedState = !isSelected;

    onSelectedInterestsChange([newInterest], newSelectedState);
    this.logInterestClick(newSelectedState);
    this.setState({ interestsToFlip: new Set([newInterest.id]) });

    this.contextualUpdateInterests(new Set([newInterest.id]));
  };

  contextualUpdateInterests(newlySelectedInterestIds: Set<string>) {
    // addedContextualTopics contains the topics that have already had their contextual topics added
    const { defaultInterests, defaultInterestsSet, addedContextualTopics } = this.state;

    const updatedInterests = [];
    const interestsToInsertAtEnd = [];
    let shouldUpdateInterestsList = false;

    defaultInterests.forEach((interest) => {
      updatedInterests.push(interest);
      defaultInterestsSet.add(interest.id);
      if (newlySelectedInterestIds.has(interest.id) && !addedContextualTopics.has(interest.id)) {
        // Currently this experiment is activated on the backend.
        // Future experiments might still need to be activated here.

        // contextual_interests for an interest should be unique, but
        // contextual interests might be repeated across different selected interests
        const dedupedSimilarInterests = interest.contextual_interests.filter(
          (interest) => !defaultInterestsSet.has(interest.id),
        );
        if (dedupedSimilarInterests.length > 0) {
          if (interest.contextual_insertion_strategy === 'InsertionStrategy.INSERT_INPLACE') {
            updatedInterests.push(...dedupedSimilarInterests);
          } else if (
            interest.contextual_insertion_strategy === 'InsertionStrategy.INSERT_AT_THE_END'
          ) {
            interestsToInsertAtEnd.push(...dedupedSimilarInterests);
          }
          dedupedSimilarInterests.forEach((similarInterest) => {
            defaultInterestsSet.add(similarInterest.id);
          });
          shouldUpdateInterestsList = true;
        }
      }
    });

    if (interestsToInsertAtEnd.length > 0) {
      updatedInterests.push(...interestsToInsertAtEnd);
    }

    if (shouldUpdateInterestsList) {
      this.setState({
        addedContextualTopics: new Set([...addedContextualTopics, ...newlySelectedInterestIds]),
        defaultInterests: updatedInterests,
      });
    }
  }

  logInterestClick = (newSelectedState: boolean) => {
    const { isRenux } = this.props;

    const elementType = newSelectedState
      ? PCONST.thrift.ElementType.INTEREST_FOLLOW
      : PCONST.thrift.ElementType.INTEREST_UNFOLLOW;
    const componentType = PCONST.thrift.ComponentType.INTEREST_GRID;
    track(PCONST.thrift.EventType.CLICK, {
      component: componentType,
      element: elementType,
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
    });
  };

  componentName = NUX_PICKER;

  recordRenderLogToServer = () => {
    const renderTimer = this.renderTimers.pop();
    renderTimer.end();
    LogToServer.logToServer(LogToServer.SERVER_LOG_TYPE.RENDER_TIME, LogToServer.HTTP_METHOD.POST, {
      timing: renderTimer.getCompletedTime(),
      component: this.componentName,
    });
  };

  renderTimers = [];

  render() {
    const {
      categorized,
      handleBottomScroll,
      isRenux,
      selectedInterests,
      showTableOfContents,
    } = this.props;
    const { interestFlipMessages, interestsToFlip, numTopicsToShow } = this.state;

    const interests = this.getInterestsWithUpdatedIsFollowed();

    return (
      <NuxPickerInterestsGrid
        categorized={categorized}
        categoryProps={this.state.categoryProps}
        disableScroll={!!numTopicsToShow && numTopicsToShow <= 5}
        handleBottomScroll={handleBottomScroll}
        handleInterestClickFn={this.handleInterestClickFn}
        interests={numTopicsToShow ? interests.slice(0, numTopicsToShow) : interests}
        interestFlipMessages={interestFlipMessages}
        interestsToFlip={interestsToFlip}
        isRenux={!!isRenux}
        selectedInterests={selectedInterests}
        showTableOfContents={showTableOfContents}
      />
    );
  }
}

const NuxPickerInterestsGridContainerWithExps = (props: Props) => (
  <Experiment name="web_renux_keep_following" isEligible={props.isRenux}>
    {({ anyEnabled: keepFollowing }) => (
      <Experiment
        name="web_renux_short"
        isEligible={props.isRenux && !props.isSimpleRenux}
        activate={false}
      >
        {({ anyEnabled: makeShort, customActivate: activateShort }) => (
          <NuxPickerInterestsGridContainer
            {...props}
            keepFollowingEnabledGroup={keepFollowing}
            inShortenedEnabled={makeShort}
            activateShortenedExpFn={activateShort}
          />
        )}
      </Experiment>
    )}
  </Experiment>
);

const mapStateToProps = ({ mostRecentPin }: ReduxState): StateProps => ({
  mostRecentPinImages:
    mostRecentPin && mostRecentPin.plp_image_signatures ? mostRecentPin.plp_image_signatures : [],
});

export default connect<Props, OwnProps, StateProps, _, _, _>(
  mapStateToProps,
  () => ({}),
)(NuxPickerInterestsGridContainerWithExps);
